import { AsyncPipe, CommonModule, NgIf } from '@angular/common';
import {
  computed,
  Directive,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Signal,
  ViewChild,
} from '@angular/core';
import { DocTemplateService } from '../base-docs/detail/doc-template-selection/doc-template.service';
import {
  ConfigStateService,
  LocalizationService,
  SubscriptionService,
  LocalizationModule,
} from '@abp/ng.core';
import { BaseDocDetail } from '../base-docs/detail/base-doc-detail';
import { ClassificationTypeService, DocRole, WarehouseDto } from '@digiwall/goliath-core/proxy';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ConfirmationService, ToasterService, DisabledDirective } from '@abp/ng.theme.shared';
import { BreadcrumbService } from 'xng-breadcrumb';
import {
  ChatComponent,
  DeactivatableComponentService,
  DwDatePickerComponent,
  DwInputAutoCompleteDirective,
  DwInputDirective,
  DwInputRequiredDirective,
  DwKeyboardShortcutDirective,
  DwNavLinkDirective,
  ErrorFormControlDirective,
  OdysseyOffcanvas,
} from '@digiwall/core';
import {
  AnnexesListComponent,
  CompanySiteStore,
  InformationsAuditedEntityComponent,
  WarehouseStore,
} from '@digiwall/goliath-core';
import { Subject, takeUntil } from 'rxjs';
import { ReportViewerComponent } from '@odyssey/reporting';
import {
  NgbDropdownModule,
  NgbNav,
  NgbNavContent,
  NgbNavItem,
  NgbNavItemRole,
  NgbNavLink,
  NgbNavLinkBase,
  NgbNavOutlet,
  NgbScrollSpy,
  NgbScrollSpyFragment,
} from '@ng-bootstrap/ng-bootstrap';
import { DocReportPermission } from '@digiwall/goliath-documents/config';
import {
  AutoSizedDropdownDirective,
  CounterComponent,
  DialogBoxComponent,
  DropdownMenu,
  DropdownMenuComponent,
  DwNgSelectInputReadonlyDirective,
  PanelComponent,
  SidePanelComponent,
} from '@digiwall/ui-kit';
import {
  DwGridDetailNavigationComponent,
  DwScrollSpyDirective,
  DwTabsComponent,
} from '@digiwall/ag-grid';
import {
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import { DocClassificationsComponent } from '../base-docs/detail/doc-classifications/doc-classifications.component';
import { DocCustomerOthersComponent } from '../base-docs/detail/doc-customer-others/doc-customer-others.component';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import { SalesPersonStore } from '@digiwall/goliath-contacts';
import {
  CreateStockDocDto,
  DayBookService,
  StockDocDefaultValueDto,
  StockDocDto,
} from '@digiwall/goliath-documents/proxy';
import { IBaseDocService } from '../base-docs/detail/i-base-doc-service';
import {
  StockDocDaybookNumberCardComponent,
  StockDocFollowUpCardComponent,
  StockDocQuantitativeDataCardComponent,
  StockDocSiteWarehouseCardComponent,
} from '../../components';

export const StockDocDetailComponentProviders = [DocTemplateService, SubscriptionService];
export const StockDocDetailComponentImports = [
  CommonModule,
  PanelComponent,
  DwTabsComponent,
  NgbNav,
  NgbNavItem,
  NgbNavItemRole,
  NgbNavLink,
  NgbNavLinkBase,
  DwNavLinkDirective,
  NgbNavContent,
  NgbScrollSpy,
  ReactiveFormsModule,
  NgxValidateCoreModule,
  DwKeyboardShortcutDirective,
  NgbScrollSpyFragment,
  DwScrollSpyDirective,
  DwInputRequiredDirective,
  NgSelectComponent,
  AutoSizedDropdownDirective,
  DwNgSelectInputReadonlyDirective,
  ErrorFormControlDirective,
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
  DwDatePickerComponent,
  DwInputDirective,
  DwInputAutoCompleteDirective,
  DocClassificationsComponent,
  DocCustomerOthersComponent,
  AnnexesListComponent,
  NgbNavOutlet,
  SidePanelComponent,
  InformationsAuditedEntityComponent,
  NgIf,
  FormsModule,
  DisabledDirective,
  DialogBoxComponent,
  ReportViewerComponent,
  AsyncPipe,
  LocalizationModule,
  DropdownMenuComponent,
  NgbDropdownModule,
  CounterComponent,
  ChatComponent,
  DwGridDetailNavigationComponent,
  StockDocSiteWarehouseCardComponent,
  StockDocDaybookNumberCardComponent,
  StockDocFollowUpCardComponent,
  StockDocQuantitativeDataCardComponent,
];

@Directive()
export abstract class StockDocDetailComponent<
    TDocService extends IBaseDocService<TDocDto, TCreateUpdateDto, TDefaultValueDto>,
    TDocDto extends StockDocDto,
    TCreateUpdateDto extends CreateStockDocDto,
    TDefaultValueDto extends StockDocDefaultValueDto,
  >
  extends BaseDocDetail<TDocService, TDocDto, TCreateUpdateDto, TDefaultValueDto>
  implements OnInit, OnDestroy
{
  @ViewChild('report') reportViewer: ReportViewerComponent;

  protected _document: TDocDto;
  public get document() {
    return this.entity;
  }

  public get saveFromExtends() {
    return super.save;
  }

  public get afterSaveFromExtends() {
    return super.afterSave;
  }

  public get navigateAfterSaveFromExtends() {
    return super.navigateAfterSave;
  }

  companySiteStore = inject(CompanySiteStore);
  salesPersonStore = inject(SalesPersonStore);
  wareHouseStore = inject(WarehouseStore);

  abstract get docRole(): DocRole;

  warehouses: WarehouseDto[];
  buttonLabel = computed(() => (this.isCreationMode() ? '::Save' : '::ToValidate'));

  destroy$ = new Subject<void>();

  actions: Signal<DropdownMenu> = computed(() => ({
    groups: [
      [
        {
          label: '::GeneratePdf',
          icon: 'digi-out-pdf-file',
          action: () => this.generatePdf(this.reportViewer),
        },
      ],
      [
        {
          label: 'Doc::GenerateDocPrintTemplate',
          icon: 'digi-out-edit-square',
          action: () => this.openDesigner(),
          requiredPolicy: DocReportPermission.Create,
        },
      ],
    ],
  }));

  constructor(
    route: ActivatedRoute,
    router: Router,
    subscriptionService: SubscriptionService,
    docService: TDocService,
    formBuilder: FormBuilder,
    toastService: ToasterService,
    breadcrumbService: BreadcrumbService,
    dayBookService: DayBookService,
    localizationService: LocalizationService,
    classificationTypeService: ClassificationTypeService,
    docTemplateService: DocTemplateService,
    injector: Injector,
    config: ConfigStateService,
    confirmationService: ConfirmationService,
    deactivableComponentService: DeactivatableComponentService,
  ) {
    super(
      breadcrumbService,
      dayBookService,
      classificationTypeService,
      formBuilder,
      localizationService,
      docService,
      toastService,
      router,
      subscriptionService,
      docTemplateService,
      injector,
      config,
      confirmationService,
      deactivableComponentService,
      route,
    );
  }

  protected override setDefaultValue(response: StockDocDefaultValueDto) {
    this.entity = this.createEntity(Object.assign(this.defaultValue, response));
    this.form.reset(this.entity);
    this.isStockValidated.set(false);
    this.initBreadcrumb();
    this.entitySignal.set(this.entity);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.init();
  }

  override get formModel() {
    return Object.assign({}, this.baseForm, {
      warehouseId: [null],
      siteToId: [null],
      warehouseToId: [null],
      companySiteId: [null, Validators.required],
      currencyCode: [null],
    });
  }

  protected override getEntityCallback(): void {
    super.getEntityCallback();
    this.setWareHouses(this.entity.companySiteId);
  }
  protected override initFormObservers() {
    this.form
      .get('companySiteId')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(companySiteId => {
        this.setWareHouses(companySiteId);
      });
  }

  entityLinesNotEmpty = computed(
    () =>
      this.entitySignal()?.lines != null &&
      this.entitySignal()?.lines.some(x => (x as any).stockLocation != null),
  );

  private setWareHouses(companySiteId: string) {
    this.warehouses = this.wareHouseStore
      .entities()
      .filter(wh => wh.companySiteId == companySiteId);

    if (companySiteId == null) {
      this.form.get('warehouseId').setValue(null);
    }
  }
}
